import { DateTime } from "luxon";

import { DashboardTargetEntryDTO } from "../../../DTOs/Dashboards/DashboardTargetsCardDataDTO";
import { TargetDirection } from "../../../DTOs/Targets/TargetJudgement";
import {
  MaterialMovementCardDataDTO,
  MaterialMovementCardSectionDTO,
} from "../../../DTOs/Dashboards/MaterialMovementCardDataDTO";
import { TargetsCardActualDTO } from "../../../DTOs/Dashboards/DrillBlastCardDataDTO";

export type TargetMetricKind = "additive" | "rate";

export type TargetGaugeStatus = "no-target" | "on-track" | "behind" | "in-band" | "low" | "high" | "not-judged";

export interface TargetGaugeInput {
  key: string;
  label: string;
  unit: string;
  kind: TargetMetricKind;
  target: number | null;
  actual: number;
  elapsed: number;
  direction?: TargetDirection;
  bandTolerancePercent?: number | null;
}

export interface TargetGauge {
  key: string;
  label: string;
  unit: string;
  kind: TargetMetricKind;
  target: number | null;
  actual: number;
  attainment: number | null;
  expected: number | null;
  fill: number;
  band: { from: number; to: number } | null;
  status: TargetGaugeStatus;
}

const TOLERANCE = 0.01; // Note: a shortfall this small is rounding in the aggregates rather than a mine falling behind.

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export const elapsedFraction = (start: DateTime, end: DateTime, now: DateTime): number => {
  const span = end.toMillis() - start.toMillis();
  if (span <= 0) return 0;

  return clamp((now.toMillis() - start.toMillis()) / span, 0, 1);
};

// Note: a band gauge's arc runs to the top of the band rather than to the target, so the band sits inside the arc and
// overshooting it shows as a full arc marked high.
export const buildTargetGauge = ({
  key,
  label,
  unit,
  kind,
  target,
  actual,
  elapsed,
  direction = TargetDirection.HigherIsBetter,
  bandTolerancePercent,
}: TargetGaugeInput): TargetGauge => {
  const base = { key, label, unit, kind, target, actual };

  if (target === null || target <= 0)
    return { ...base, attainment: null, expected: null, fill: 0, band: null, status: "no-target" };

  const attainment = actual / target;

  if (direction === TargetDirection.Band) {
    const tolerance = (bandTolerancePercent ?? 0) / 100;
    const top = 1 + tolerance;
    return {
      ...base,
      attainment,
      expected: null,
      fill: clamp(attainment / top, 0, 1),
      band: { from: (1 - tolerance) / top, to: 1 },
      status: attainment < 1 - tolerance - TOLERANCE ? "low" : attainment > top + TOLERANCE ? "high" : "in-band",
    };
  }

  const expected = kind === "additive" ? clamp(elapsed, 0, 1) : null;
  const threshold = expected ?? 1;
  const status: TargetGaugeStatus = direction === TargetDirection.Neutral ? "not-judged"
    : direction === TargetDirection.LowerIsBetter ? (attainment <= threshold + TOLERANCE ? "on-track" : "behind")
    : (attainment >= threshold - TOLERANCE ? "on-track" : "behind");

  return { ...base, attainment, expected, fill: clamp(attainment, 0, 1), band: null, status };
};

export interface TargetActualSources {
  materialMovement: MaterialMovementCardDataDTO | null;
  formulaActuals?: TargetsCardActualDTO[] | null;
}

const sectionRow = (section: MaterialMovementCardSectionDTO | undefined, label: string) =>
  section?.rows?.find(row => row.label === label)?.both ?? null;

const sectionTotal = (section: MaterialMovementCardSectionDTO | undefined) =>
  section ? section.rows.reduce((total, row) => total + (row.both ?? 0), 0) : null;

const ACTUAL_SOURCES: Record<string, (sources: TargetActualSources) => number | null> = {
  "ore-tonnes": ({ materialMovement }) => sectionRow(materialMovement?.production, "Ore"),
  "waste-tonnes": ({ materialMovement }) => sectionRow(materialMovement?.production, "Waste"),
  "rehandle-tonnes": ({ materialMovement }) => sectionTotal(materialMovement?.rehandle),
  "crushing-tonnes": ({ materialMovement }) => sectionTotal(materialMovement?.crushing),
};

// Note: the movement metrics pair with the card that shows them; any other type an admin put on the card has a formula,
// and the server sends its actual under the type's system key.
const actualOf = (key: string, sources: TargetActualSources) =>
  ACTUAL_SOURCES[key]?.(sources) ?? sources.formulaActuals?.find(actual => actual.key === key)?.actual ?? null;

export const buildTargetGauges = (
  targets: DashboardTargetEntryDTO[],
  sources: TargetActualSources,
  elapsed: number
): TargetGauge[] =>
  targets.flatMap(entry => {
    const actual = actualOf(entry.key, sources);
    if (actual === null) return [];

    return buildTargetGauge({
      key: entry.key,
      label: entry.label,
      unit: entry.unit,
      kind: entry.isRate ? "rate" : "additive",
      target: entry.target,
      actual,
      elapsed,
      direction: entry.direction,
      bandTolerancePercent: entry.bandTolerancePercent,
    });
  });

export const TARGET_METRIC_COUNT = Object.keys(ACTUAL_SOURCES).length;

export const loadingGaugeCount = (shownKeys: string[] | null) => shownKeys?.length ?? TARGET_METRIC_COUNT;

// Note: null means every metric is shown, so the stored selection does not go stale when the set of
// metrics changes. Turning the last one back on returns to null rather than a list that happens to
// hold them all.
export const toggleGaugeKey = (
  shownKeys: string[] | null,
  allKeys: string[],
  key: string
): string[] | null => {
  const shown = shownKeys ?? allKeys;
  const next = shown.includes(key)
    ? shown.filter(shownKey => shownKey !== key)
    : allKeys.filter(allKey => shown.includes(allKey) || allKey === key);

  return next.length === allKeys.length ? null : next;
};
